import { ReactComponent as CloseSvg } from "@/src/assets/icons/icon-close-red.svg";
import { ReactComponent as InfoSvg } from "@/src/assets/icons/icon-Info-blue4-fill.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import Typo from "@/src/components/atom/Typo";
import CallOut from "@/src/components/molecule/CallOut";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { authApi } from "@/src/store/apis/auth";
import {
  subscriptionApi,
  useRetryPaymentMutation,
  useUpdateSubscriptionMutation,
} from "@/src/store/apis/subscription";
import { FreeCodeDto } from "@/src/store/apis/subscription/interface";
import colorSet, { ColorType } from "@/src/styles/color";
import { addZeroPrefix } from "@/src/utils/addZeroPrefix";
import { isNull, isUndefined } from "@/src/utils/is";
import dayjs from "dayjs";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Flex from "@/src/components/molecule/Flex";
import { useAppDispatch, useAppSelector } from "@/src/store";
import { setRetryPaymentClose } from "@/src/store/slice/auth";
import { getTimezoneDateWithOffset } from "@/src/hooks/useCompanyLocalDate";
import { calculateRemainingDays } from "@/src/utils/subscriptionDate";
import { calculatePlanChangeDate } from "@/src/utils/subscription";
import ChangePlanInfoDialog from "./dialog/ChangePlanInfoDialog";

type CalloutStatusType =
  | "PAYMENT_DUE_IN_ONE_WEEK_MONTHLY_TYPE"
  | "PAYMENT_DUE_IN_ONE_WEEK_ANNUALLY_TYPE"
  | "FREE_PLAN"
  | "PAYMENT_FAIL"
  | "NONE_SUBSCRIBE"
  | "PLAN_CHANGE"
  | "PLAN_TERMINATION"
  | "RETRACT_FREE_CODE"
  | "NOT_PAID_YET"
  | null;

enum DialogState {
  NULL,
  PLAN_TERMINATION,
  PLAN_CHANGE,
}

interface SubscriptionStatusCalloutProps {
  subscriptionIds: number[];
  onSubscriptionIdsChange: Dispatch<SetStateAction<number[]>>;
  freeCodeIds?: number[];
  onFreeCodeIdsChange?: Dispatch<SetStateAction<number[]>>;
  type?: CalloutStatusType;
  onPlanSubscriptionClick?: () => void;
}

const emptyFreeCodeArray: FreeCodeDto[] = [];

function SubscriptionStatusCallout({
  subscriptionIds,
  onSubscriptionIdsChange,
  freeCodeIds = [],
  onFreeCodeIdsChange,
  type,
  onPlanSubscriptionClick,
}: SubscriptionStatusCalloutProps) {
  const { t } = useTranslation();
  const alert = useAlert();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const retryPaymentClose = useAppSelector(
    (state) => state.auth.retryPaymentClose,
  );

  const [calloutStatus, setCalloutStatus] = useState<CalloutStatusType>(null);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [isManuallyDismissed, setIsManuallyDismissed] = useState(false);

  // API
  const session = authApi.endpoints.getSession.useQueryState(undefined);
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [retryPayment, { isLoading: isRetryPaymentLoading }] =
    useRetryPaymentMutation();

  const { subscriptionId, subscriptionNo } =
    subscriptionApi.endpoints.getSubscriptions.useQueryState(
      { page: 1, pageSize: 10 },
      {
        selectFromResult: ({ currentData }) => {
          return {
            subscriptionId: currentData?.rows[0].id,
            subscriptionNo: currentData?.rows[0].subscriptionNo,
          };
        },
      },
    );

  const { paymentStatus, paymentNo } =
    subscriptionApi.endpoints.getPayments.useQueryState(
      {
        page: 1,
        pageSize: 1,
      },
      {
        selectFromResult: ({ currentData }) => {
          return {
            paymentStatus: currentData?.rows[0].paymentStatus,
            paymentNo: currentData?.rows[0].paymentNo,
          };
        },
      },
    );

  const {
    subscriptionStatus,
    subscriptionEndAtTo,
    subscriptionStartAtFrom,
    nextPlan,
    currentPlanName,
    paymentCycle,
    subscriptionType,
    retractFreeCodeReason,
    freeCodeList,
    isUnstable,
    isFetching,
    annualPaymentInstallment,
  } = subscriptionApi.endpoints.getSubscription.useQueryState(
    { id: subscriptionId || 0 },
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isFetching || isError || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          subscriptionStatus: currentData?.subscriptionStatus,
          subscriptionType: currentData?.subscriptionType,
          subscriptionStartAtFrom: currentData?.subscriptionStartAtFrom,
          subscriptionEndAtTo: currentData?.subscriptionEndAtTo,
          currentPlanName: currentData?.plan.name,
          nextPlan: currentData?.nextPlan,
          paymentCycle: currentData?.paymentCycle,
          retractFreeCodeReason: currentData?.retractFreeCodeReason || "",
          freeCodeList:
            isStable && currentData?.freeCodeList
              ? currentData?.freeCodeList
              : emptyFreeCodeArray,
          isUnstable,
          currentData,
          isFetching,
          annualPaymentInstallment: currentData?.annualPaymentInstallment,
        };
      },
    },
  );

  const freeCodeCopyList = [...freeCodeList].reverse();
  const isManagerType =
    session.currentData?.row.exporterUserType === "MANAGER" ||
    session.currentData?.row.exporterUserType === "MIDDLE_MANAGER";

  // 남은 일수 계산 (시간, 분, 초까지 포함한 정확한 계산)
  const duration = subscriptionEndAtTo
    ? calculateRemainingDays(subscriptionEndAtTo)
    : 0;

  // 만료 여부 체크 (시간, 분, 초까지 정확히 비교)
  const isExpired = subscriptionEndAtTo
    ? dayjs().isAfter(dayjs(subscriptionEndAtTo))
    : true;
  const isUnstableNextPlan = isNull(nextPlan);
  const isNextPlan = !isUnstableNextPlan;
  const isFreeCodeIds = freeCodeIds.includes(freeCodeCopyList[0]?.id);

  const handleResubscribeButtonClick = () => {
    // 모달로 변경: 기존 페이지 이동 대신 모달 오픈
    if (onPlanSubscriptionClick) {
      onPlanSubscriptionClick();
    } else {
      // fallback: 기존 방식
      navigate(
        `${EXPORTER_PRIVATE_PATH.SUBSCRIPTION_PLAN}?isInquiry=true`,
      );
    }
  };

  const handleButtonClick = (buttonType: CalloutStatusType) => {
    if (buttonType === "PLAN_TERMINATION") {
      setDialogState(DialogState.PLAN_TERMINATION);
    }
    if (buttonType === "PLAN_CHANGE") {
      setDialogState(DialogState.PLAN_CHANGE);
    }
  };

  const handleRetryPaymentClick = async () => {
    try {
      await retryPayment().unwrap();
      dispatch(setRetryPaymentClose(true));
      alert.showAlert({
        type: "success",
        message: t("alert:subscriptionPaymentSuccess"),
      });
    } catch (e: any) {
      alert.showAlert({
        type: "error",
        message: t("alert:paymentFailedCheckReason"),
      });
    }
  };

  const handleRetryPaymentCloseClick = () => {
    dispatch(setRetryPaymentClose(true));
    setCalloutStatus(null);
  };

  const handleCalloutCloseClick = () => {
    // 세션 동안만 알림 숨기기 (새로고침하면 다시 표시)
    setCalloutStatus(null);
    setIsManuallyDismissed(true);
  };

  const handlePlanTerminationCancelClick = async () => {
    if (!subscriptionId) {
      return;
    }

    try {
      await updateSubscription({
        id: subscriptionId,
        subscriptionStatus: "PROGRESS",
      }).unwrap();

      setDialogState(DialogState.NULL);

      alert.showAlert({
        type: "success",
        message: t(
          "subscriptionManagement:subscription.alert.subscriptionCancellation",
        ),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data?.message)
        ? e.data?.message[0]
        : e.data?.message;
      alert.showAlert({
        type: "error",
        message: message,
      });
    }
  };

  const renderDialog = () => {
    if (dialogState === DialogState.PLAN_TERMINATION) {
      return (
        <AlertDialog
          title={t("subscriptionManagement:subscription.cancelPlanChange")}
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          okText={t("subscriptionManagement:subscription.button.ok")}
          cancelText={t("subscriptionManagement:subscription.button.cancel")}
          onOk={handlePlanTerminationCancelClick}
        >
          {t(
            "subscriptionManagement:subscription.areYouSureYouWantToCancelThePlanTermination",
          )}
        </AlertDialog>
      );
    }

    if (dialogState === DialogState.PLAN_CHANGE) {
      return (
        <ChangePlanInfoDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
        />
      );
    }

    return <></>;
  };

  const renderCallout = () => {
    const year = getTimezoneDateWithOffset({
      date: subscriptionEndAtTo as string,
      gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
    }).get("year");
    const month = addZeroPrefix(
      getTimezoneDateWithOffset({
        date: subscriptionEndAtTo as string,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
      }).get("month") + 1,
    );
    const date = addZeroPrefix(
      getTimezoneDateWithOffset({
        date: subscriptionEndAtTo as string,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
      }).get("date"),
    );
    const rePaymentDate = getTimezoneDateWithOffset({
      date: subscriptionStartAtFrom as string,
      gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
    })
      .add(48, "hour")
      .subtract(1, "second")
      .format(DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm);

    if (isFetching) {
      return null;
    }

    if (isUnstable) {
      return (
        <StyledCallOut borderColor="blue9" backgroundColor="blue10">
          <InfoIcon color="blue4" />
          <Flex alignItems="center" justifyContent="space-between" isFullWidth>
            <Typo color="indigo" typoType="b9r">
              {t(
                "subscriptionManagement:subscription.thereIsNoCurrentSubscriptionPlan",
              )}
            </Typo>
            <Button
              buttonSize={32}
              onClick={handleResubscribeButtonClick}
              disabled={isManagerType}
            >
              {t("subscriptionManagement:subscription.button.planSubscription")}
            </Button>
          </Flex>
        </StyledCallOut>
      );
    }

    switch (calloutStatus) {
      // 월간 구독시
      case "PAYMENT_DUE_IN_ONE_WEEK_MONTHLY_TYPE":
        return (
          <StyledCallOut borderColor="blue9" backgroundColor="blue10">
            <InfoIcon color="blue4" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="indigo" typoType="b9r">
                {t("common:paymentScheduledCheck", { day: duration })}
              </Typo>
              <CloseIcon color="blue4" onClick={handleCalloutCloseClick} />
            </Flex>
          </StyledCallOut>
        );
      // 연간 구독시
      case "PAYMENT_DUE_IN_ONE_WEEK_ANNUALLY_TYPE":
        return (
          <StyledCallOut borderColor="blue9" backgroundColor="blue10">
            <InfoIcon color="blue4" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="indigo" typoType="b9r">
                {t("common:paymentIsScheduledInOneWeek", {
                  year: year.toString(),
                  month: month.toString(),
                  date: date.toString(),
                })}
              </Typo>
              <CloseIcon color="blue4" onClick={handleCalloutCloseClick} />
            </Flex>
          </StyledCallOut>
        );
      // 무료이용기간 1주일 이내 남음
      case "FREE_PLAN":
        return (
          <StyledCallOut borderColor="blue9" backgroundColor="blue10">
            <InfoIcon color="blue4" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="indigo" typoType="b9r">
                {t("common:freeServiceExpiration", {
                  day: duration,
                })}
              </Typo>
              <CloseIcon color="blue4" onClick={handleCalloutCloseClick} />
            </Flex>
          </StyledCallOut>
        );
      // 결제 중단 예정
      case "NOT_PAID_YET":
        return (
          <StyledCallOut borderColor="red7" backgroundColor="red8">
            <InfoIcon color="red2" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="red2" typoType="b9r">
                {t("common:paymentFailedNotice", {
                  day: rePaymentDate,
                })}
              </Typo>
              <Flex alignItems="center" gap={4}>
                <Button
                  buttonSize={32}
                  disabled={isManagerType || isRetryPaymentLoading}
                  buttonGrade="tertiary"
                  buttonColor="black"
                  onClick={handleRetryPaymentClick}
                  isLoading={isRetryPaymentLoading}
                >
                  {t("common:retryPayment")}
                </Button>
                <CloseIcon
                  color="red2"
                  onClick={handleRetryPaymentCloseClick}
                />
              </Flex>
            </Flex>
          </StyledCallOut>
        );
      // 결제 실패
      case "PAYMENT_FAIL":
        return (
          <StyledCallOut borderColor="red7" backgroundColor="red8">
            <InfoIcon color="red2" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="red2" typoType="b9r">
                {t(
                  "subscriptionManagement:subscription.paymentFailedPleaseCheckYourPaymentInformation",
                )}
              </Typo>
              <Button
                buttonSize={32}
                disabled={isManagerType}
                onClick={handleResubscribeButtonClick}
              >
                {t(
                  "subscriptionManagement:subscription.button.planSubscription",
                )}
              </Button>
            </Flex>
          </StyledCallOut>
        );
      // 즉시해제 (구독이 없는 상태)
      case "NONE_SUBSCRIBE":
        return (
          <StyledCallOut borderColor="blue9" backgroundColor="blue10">
            <InfoIcon color="blue4" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="indigo" typoType="b9r">
                {t(
                  "subscriptionManagement:subscription.thereIsNoCurrentSubscriptionPlan",
                )}
              </Typo>
              <Button
                buttonSize={32}
                onClick={handleResubscribeButtonClick}
                disabled={isManagerType}
              >
                {t(
                  "subscriptionManagement:subscription.button.planSubscription",
                )}
              </Button>
            </Flex>
          </StyledCallOut>
        );
      // 플랜이 변경 되었을때
      case "PLAN_CHANGE":
        const nextPlanName = nextPlan?.name ?? "-";

        // 플랜 변경일 = 구독 종료일 + 1일
        const nextDate = calculatePlanChangeDate(
          subscriptionEndAtTo as string,
          session.currentData?.row.exporter.gmtOffset || 0
        );

        return (
          <StyledCallOut borderColor="blue9" backgroundColor="blue10">
            <InfoIcon color="blue4" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="indigo" typoType="b9r">
                {t("common:subscriptionToChangePlan", {
                  year: nextDate.get("year").toString(),
                  month: addZeroPrefix(nextDate.get("month") + 1).toString(),
                  date: addZeroPrefix(nextDate.get("date")).toString(),
                  nextPlanName,
                })}
              </Typo>
              <Button
                buttonSize={32}
                buttonColor="black"
                buttonGrade="tertiary"
                onClick={() => handleButtonClick("PLAN_CHANGE")}
                disabled={isManagerType}
              >
                {t("subscriptionManagement:subscription.changePlanInformation")}
              </Button>
            </Flex>
          </StyledCallOut>
        );
      // 해지예정
      case "PLAN_TERMINATION":
        return (
          <StyledCallOut borderColor="blue9" backgroundColor="blue10">
            <InfoIcon color="blue4" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="indigo" typoType="b9r">
                {t("common:subscriptionCanceledCancel", {
                  year: year.toString(),
                  month: month.toString(),
                  date: date.toString(),
                  currentPlanName: currentPlanName as string,
                })}
              </Typo>
              <Button
                buttonSize={32}
                buttonColor="black"
                buttonGrade="tertiary"
                onClick={() => handleButtonClick("PLAN_TERMINATION")}
                disabled={isManagerType}
              >
                {t(
                  "subscriptionManagement:subscription.button.cancelPlanChange",
                )}
              </Button>
            </Flex>
          </StyledCallOut>
        );
      // 무료코드 회수
      case "RETRACT_FREE_CODE":
        return (
          <StyledCallOut borderColor="red7" backgroundColor="red8">
            <InfoIcon color="red2" />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              isFullWidth
            >
              <Typo color="red2" typoType="b9r">
                {t(
                  "subscriptionManagement:subscription.freePeriodCodeUsageStoppedDueTo",
                  { retractFreeCodeReason },
                )}
              </Typo>
              <CloseIcon color="red2" onClick={handleCalloutCloseClick} />
            </Flex>
          </StyledCallOut>
        );

      case null:
      default:
        return <></>;
    }
  };

  /**
   * 구독 상태에 따른 경고 배너 표시 여부를 결정합니다.
   *
   * @description
   * 구독 상태와 만료일을 기반으로 표시할 경고 배너를 결정합니다.
   * - `PAYMENT_FAIL`: 결제 실패 상태 (만료 전에만 경고 표시)
   * - `NOT_PAID_YET`: 재결제 대기 상태 (만료 전에만 경고 표시)
   * - `CANCELED`: 구독 취소 (만료 전: 해지예정, 만료 후: 구독 없음)
   * - `PROGRESS`: 정상 구독 (7일 이내: 갱신 안내)
   *
   * **버그 수정 (Issue #1):** 만료된 구독에 대한 경고 메시지가 계속 표시되던 문제를 수정합니다.
   *
   * @see {@link https://github.com/your-repo/issues/1}
   * @returns {void} `calloutStatus` 상태를 업데이트합니다.
   *
   * @example
   * // PAYMENT_FAIL 상태에서 만료 전
   * subscriptionStatus = "PAYMENT_FAIL"
   * isExpired = false
   * // → setCalloutStatus("PAYMENT_FAIL") // 결제 실패 경고 표시
   *
   * @example
   * // PAYMENT_FAIL 상태에서 만료 후
   * subscriptionStatus = "PAYMENT_FAIL"
   * isExpired = true
   * // → setCalloutStatus("NONE_SUBSCRIBE") // 구독 없음 안내 표시
   */
  const getCalloutStatus = useCallback(() => {
    // 무료기간 코드 회수
    if (type === "RETRACT_FREE_CODE" && !isFreeCodeIds) {
      return setCalloutStatus("RETRACT_FREE_CODE");
    }

    if (!type) {
      if (
        subscriptionStatus === "NOT_PAID_YET" &&
        !retryPaymentClose &&
        paymentNo === subscriptionNo &&
        paymentStatus === "PAYMENT_FAIL" &&
        !isExpired
      ) {
        return setCalloutStatus("NOT_PAID_YET");
      }
      if (subscriptionStatus === "PAYMENT_FAIL") {
        if (!isExpired) {
          // 결제 실패 (만료 전)
          return setCalloutStatus("PAYMENT_FAIL");
        }
        // 만료 시간 지남 - 즉시해제
        return setCalloutStatus("NONE_SUBSCRIBE");
      }
      if (subscriptionStatus === "CANCELED") {
        if (!isExpired) {
          // 아직 만료 시간 전 - 해지예정
          return setCalloutStatus("PLAN_TERMINATION");
        }
        // 만료 시간 지남 - 즉시해제
        return setCalloutStatus("NONE_SUBSCRIBE");
      }
      if (isNextPlan) {
        // 플랜 변경
        return setCalloutStatus("PLAN_CHANGE");
      }
      if (subscriptionStatus === "PROGRESS" && duration <= 7) {
        // 무료기간 7일전
        if (subscriptionType === "FREE") {
          return setCalloutStatus("FREE_PLAN");
        }
        // 결제 7일전 (연간) - 12회차일 때만 실제 결제 발생
        if (paymentCycle === "ANNUALLY" && annualPaymentInstallment === 12) {
          return setCalloutStatus("PAYMENT_DUE_IN_ONE_WEEK_ANNUALLY_TYPE");
        }
        // 결제 7일전 (월간)
        if (paymentCycle === "MONTHLY") {
          return setCalloutStatus("PAYMENT_DUE_IN_ONE_WEEK_MONTHLY_TYPE");
        }
      }
    }

    return setCalloutStatus(null);
  }, [
    type,
    isFreeCodeIds,
    subscriptionStatus,
    retryPaymentClose,
    paymentNo,
    subscriptionNo,
    paymentStatus,
    isNextPlan,
    duration,
    isExpired,
    subscriptionType,
    paymentCycle,
    annualPaymentInstallment,
  ]);

  useEffect(() => {
    // 수동으로 닫았거나 calloutStatus가 이미 설정되어 있으면 재실행하지 않음
    if (!isManuallyDismissed && calloutStatus === null && subscriptionId) {
      setTimeout(() => {
        getCalloutStatus();
      }, 0);
    }
  }, [getCalloutStatus, subscriptionId, calloutStatus, isManuallyDismissed]);

  return (
    <>
      {renderCallout()}
      {renderDialog()}
    </>
  );
}

export default SubscriptionStatusCallout;

const StyledCallOut = styled(CallOut)`
  align-items: center;
`;

const InfoIcon = styled(InfoSvg)<{ color: ColorType }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  * {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const CloseIcon = styled(CloseSvg)<{ color: ColorType }>`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;

  * {
    fill: ${({ color }) => colorSet[color]};
  }
`;
